import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Search, DollarSign, Users, BarChart2, LogOut } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { useDispatch } from 'react-redux';
import { auth } from './firebase';
import { clearUser } from './redux/authSlice';
import './Dashboard.css';

const FEATURES = [
  {
    icon: DollarSign,
    title: 'Real Pricing Data',
    desc: 'See what other IT teams actually paid — not the inflated list price vendors quote.',
  },
  {
    icon: Users,
    title: 'Community Intelligence',
    desc: 'Crowdsourced quotes from hundreds of IT Admins and MSPs across every major SaaS category.',
  },
  {
    icon: Search,
    title: 'Instant Lookup',
    desc: 'Search any vendor and see average per-seat costs, discount trends, and tier breakdowns instantly.',
  },
  {
    icon: BarChart2,
    title: 'Negotiation Leverage',
    desc: 'Walk into renewal calls knowing what comparable companies paid. Stop leaving money on the table.',
  },
];

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated, isLoading } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/login" />;
  }

  if (isLoading || !user) {
    return <div className="dashboard-container"><div className="loading-text">Loading...</div></div>;
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      dispatch(clearUser());
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      // Post to Mailchimp API via a simple form POST (no jQuery, no external script)
      const formData = new FormData();
      formData.append('EMAIL', trimmed);
      formData.append('b_0401be36d4d6d01746846f1fc_501a13290f', ''); // honeypot

      await fetch(
        'https://vendorscout.us21.list-manage.com/subscribe/post?u=0401be36d4d6d01746846f1fc&id=501a13290f&f_id=005687e6f0',
        { method: 'POST', body: formData, mode: 'no-cors' }
      );
      // no-cors means we can't read the response — assume success
      setSubmitted(true);
    } catch {
      setFormError('Something went wrong. Please try again or email support@vendorscout.net.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard-container">
      <button className="dash-waitlist-logout" onClick={handleLogout} title="Sign out">
        <LogOut size={16} />
      </button>

      <div className="waitlist-hero">
        <div className="waitlist-badge">You&apos;re on the list</div>
        <h1>Here&apos;s what&apos;s coming for you.</h1>
        <p className="waitlist-subtitle">
          Welcome, {user.email}. VendorScout beta is launching in waves — you&apos;ll be among the first IT pros to get access.
        </p>
      </div>

      <div className="waitlist-features">
        {FEATURES.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="waitlist-feature-card">
            <div className="waitlist-feature-icon">
              <Icon size={20} />
            </div>
            <div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="waitlist-cta-box">
        {submitted ? (
          <div className="waitlist-success">
            <span className="waitlist-success-icon">✓</span>
            <h3>You&apos;re confirmed!</h3>
            <p>We&apos;ll email you at <strong>{email || user.email}</strong> when your beta access is ready.</p>
          </div>
        ) : (
          <>
            <h2>Get notified the moment your access opens</h2>
            <p>Drop your email below — we&apos;ll reach out as soon as your beta wave launches.</p>
            <form className="waitlist-form" onSubmit={handleSubmit}>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFormError(''); }}
                className="waitlist-input"
                disabled={submitting}
              />
              <button type="submit" className="waitlist-submit-btn" disabled={submitting}>
                {submitting ? 'Sending...' : 'Notify me'}
              </button>
            </form>
            {formError && <p className="waitlist-form-error">{formError}</p>}
          </>
        )}
      </div>

      <div className="support-message">
        <p>Questions or feedback?</p>
        <a href="mailto:support@vendorscout.net" className="support-button">
          support@vendorscout.net
        </a>
      </div>
    </div>
  );
};

export default Dashboard;
